import { useEffect, useLayoutEffect, useState } from "react";
import { X, ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = [
  { target: '[data-testid="author-earnings"]', title: "Your earnings", body: "Views on your published stories accrue here in real time. Withdraw your balance to PayPal whenever it's above $1." },
  { target: '[data-testid="studio-profile-card"]', title: "Your public profile", body: "Your byline, @handle, bio and avatar — shown on every story and on your public author page. Keep it sharp." },
  { target: '[data-testid="studio-form-title"]', title: "The headline", body: "Start every story here. Clear, specific headlines get the most readers." },
  { target: '[data-testid="studio-form-summary"]', title: "The summary", body: "One or two sentences shown in story cards across the site — make it count." },
  { target: '[data-testid="studio-form-body"]', title: "Your story", body: "Write in plain paragraphs. Separate them with blank lines — we handle the formatting." },
  { target: '[data-testid="studio-save-draft"]', title: "Drafts & autosave", body: "Save privately any time — and the Studio quietly autosaves your work every minute so nothing is ever lost." },
  { target: '[data-testid="studio-submit-button"]', title: "Submit for review", body: "When you're ready, send it to the editorial desk. You'll get an email when it's approved or sent back with notes." },
  { target: '[data-testid="studio-drafts-list"]', title: "Your drafts", body: "Private drafts live here — pick one up any time to keep writing.", optional: true },
  { target: '[data-testid="studio-submissions-list"]', title: "Your submissions", body: "Track every story's status here: pending review, published, or sent back for changes.", optional: true },
];

export const StudioTour = ({ onClose }: { onClose: () => void }) => {
  const [steps] = useState(() => STEPS.filter((s) => !s.optional || document.querySelector(s.target)));
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const step = steps[i];

  useLayoutEffect(() => {
    const el = document.querySelector(step?.target || "");
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    let raf = 0;
    const measure = () => { setRect(el.getBoundingClientRect()); raf = requestAnimationFrame(measure); };
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [step]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && i < steps.length - 1) setI(i + 1);
      if (e.key === "ArrowLeft" && i > 0) setI(i - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [i, steps.length, onClose]);

  if (!step || !rect) return null;
  const pad = 8;
  const below = rect.bottom + 190 < window.innerHeight;
  const tipTop = below ? rect.bottom + pad + 10 : Math.max(12, rect.top - pad - 185);
  const tipLeft = Math.min(Math.max(12, rect.left), window.innerWidth - 372);

  return (
    <div className="fixed inset-0 z-[95]" data-testid="studio-tour" aria-modal role="dialog">
      <div className="absolute rounded-xl pointer-events-none transition-all duration-300"
        style={{
          top: rect.top - pad, left: rect.left - pad,
          width: rect.width + pad * 2, height: rect.height + pad * 2,
          boxShadow: "0 0 0 9999px rgba(2,2,6,0.82), 0 0 0 1.5px #7C3AED, 0 0 34px rgba(124,58,237,0.5)",
        }} />
      <div className="absolute w-[360px] bg-[#0d0d12] border border-[#7C3AED]/40 rounded-xl p-5 shadow-[0_16px_60px_rgba(0,0,0,0.6)] transition-all duration-300"
        style={{ top: tipTop, left: tipLeft }} data-testid="studio-tour-tooltip">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#A78BFA]">
            Studio tour · {i + 1}/{steps.length}
          </span>
          <button onClick={onClose} data-testid="tour-close" aria-label="Skip tour"
            className="text-gray-500 hover:text-white transition-colors"><X className="h-4 w-4" /></button>
        </div>
        <h3 className="font-heading font-bold text-white text-base mt-2">{step.title}</h3>
        <p className="text-gray-400 text-xs mt-1.5 leading-relaxed">{step.body}</p>
        <div className="flex items-center justify-between mt-4">
          <div className="flex gap-1.5">
            {steps.map((_, d) => (
              <span key={d} className={`h-1.5 rounded-full transition-all duration-200 ${d === i ? "w-4 bg-[#7C3AED]" : "w-1.5 bg-white/15"}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button onClick={() => setI(i - 1)} data-testid="tour-back"
                className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-gray-500 hover:text-white transition-colors">
                <ArrowLeft className="h-3 w-3" /> Back
              </button>
            )}
            {i < steps.length - 1 ? (
              <button onClick={() => setI(i + 1)} data-testid="tour-next"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#7C3AED] px-3 py-1.5 font-mono text-[10px] uppercase font-bold text-white hover:bg-[#6a2dd0] transition-colors">
                Next <ArrowRight className="h-3 w-3" />
              </button>
            ) : (
              <button onClick={onClose} data-testid="tour-finish"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#22C55E] px-3 py-1.5 font-mono text-[10px] uppercase font-bold text-black hover:brightness-110 transition-[filter]">
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
